import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import { Button, Image, Icon, Text, Popover } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'

// TODO: Migrate to v2
import {
  QTEChecklistStatuses,
  selectIsPastOrSameAsTaxEstimateIRSPaymentDate,
  selectIsPastTaxEstimateCalculationDate,
  selectQTEChecklistStatusAndDates,
} from '@/featuresV1/Admin/QuarterlyTaxEstimateDetails/quarterlyTaxEstimateDetails.selector'
import { TextColors, TypographyStyles } from '@/styles'

const IRSPaymentDueAt = ({
  irsPaymentDueAt,
  isPastPaymentDate,
}: {
  irsPaymentDueAt?: DateTime
  isPastPaymentDate: boolean
}) =>
  irsPaymentDueAt ? (
    <div className="flex flex-row items-center gap-2 items-center my-2">
      <Icon
        icon={regular('square-dollar')}
        className={`pr-2 ${isPastPaymentDate ? TextColors.RED : TextColors.MOSS}`}
      />
      <Text
        variant={TypographyStyles.BODY_SM}
        color={isPastPaymentDate ? TextColors.RED : TextColors.DEFAULT}
      >
        IRS payment due{' '}
        <b>{irsPaymentDueAt.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}</b>
      </Text>
    </div>
  ) : null

const ChecklistDueAt = ({
  checkListDueAt,
  displayText,
  isPastCalculationDate,
}: {
  checkListDueAt: DateTime | string
  displayText: string
  isPastCalculationDate: boolean
}) => {
  if (!displayText || !checkListDueAt) return null

  const dueDate =
    typeof checkListDueAt === 'string'
      ? checkListDueAt
      : checkListDueAt.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  return (
    <div className="flex flex-row items-center gap-2 items-center my-2">
      <Icon
        icon={regular('calendar-check')}
        className="pr-2"
        color={isPastCalculationDate ? 'text-red-900' : 'text-yellow-500'}
      />
      <Text
        variant={TypographyStyles.BODY_SM}
        color={isPastCalculationDate ? TextColors.RED : TextColors.DEFAULT}
      >
        {displayText} <b>{dueDate}</b>
      </Text>
    </div>
  )
}

export const ChecklistDoneHeader = () => {
  const checklistStatus = useReselector(selectQTEChecklistStatusAndDates)

  return (
    <div className="flex flex-col items-center text-center">
      <Image
        src="https://heard-images.s3.amazonaws.com/assets/calculator2.svg"
        className="h-[100px] w-[117px] mb-4"
        alt="Checklist"
      />
      <div className="max-w-xl text-center">
        <Text variant={TypographyStyles.DISPLAY_SM}>
          We’re Calculating Your Estimate...
        </Text>
        <Text className="mt-4 mb-4">
          Thank you for completing the checklist! You should receive your
          estimate soon.
        </Text>
        <IRSPaymentDueAt
          irsPaymentDueAt={checklistStatus?.rest?.forDisplay.irsPaymentDue}
          isPastPaymentDate={false}
        />
      </div>
    </div>
  )
}

export const ChecklistOpenHeader = () => {
  const checklistStatus = useReselector(selectQTEChecklistStatusAndDates)
  const isPastCalculationDate = useReselector(
    selectIsPastTaxEstimateCalculationDate
  )
  const isPastPaymentDate = useReselector(
    selectIsPastOrSameAsTaxEstimateIRSPaymentDate
  )

  const infoText = useMemo(() => {
    switch (checklistStatus.status) {
      case QTEChecklistStatuses.checklistOpen:
        return 'Complete the checklist to receive your quarterly tax estimate'
      case QTEChecklistStatuses.calcsMayBeReceivedAfterIRSPaymentDue:
      case QTEChecklistStatuses.afterIRSPaymentDue:
      case QTEChecklistStatuses.beforeChecklistOpens:
        return ''
      default:
        return checklistStatus.status satisfies never
    }
  }, [checklistStatus])

  const [displayText, checklistDueAt]: [string, DateTime | string] =
    useMemo(() => {
      switch (checklistStatus.status) {
        case QTEChecklistStatuses.beforeChecklistOpens:
          return ['', '']
        case QTEChecklistStatuses.checklistOpen:
          return ['Checklist due', checklistStatus.endDateForDisplay]
        case QTEChecklistStatuses.calcsMayBeReceivedAfterIRSPaymentDue:
          return ['Complete by', 'ASAP']
        case QTEChecklistStatuses.afterIRSPaymentDue:
          return ['Overdue', 'Checklist']
        default:
          return checklistStatus.status satisfies never
      }
    }, [checklistStatus])

  const popupText = useMemo(() => {
    switch (checklistStatus.status) {
      case QTEChecklistStatuses.checklistOpen:
      case QTEChecklistStatuses.beforeChecklistOpens:
      case QTEChecklistStatuses.calcsMayBeReceivedAfterIRSPaymentDue:
      case QTEChecklistStatuses.afterIRSPaymentDue:
        return null
      default:
        return checklistStatus.status satisfies never
    }
  }, [checklistStatus.status])

  return (
    <div className="flex items-start gap-4">
      <div className="w-[124px] flex-shrink-0">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/green-checklist.svg"
          className="h-[120px] w-[120px]"
          alt="Checklist"
        />
      </div>
      <div className="flex-1">
        <Text variant={TypographyStyles.DISPLAY_SM} className="mb-2">
          Get Your Estimate
        </Text>
        <Text className="mb-4" color={TextColors.MUTED}>
          {infoText}
        </Text>

        {/* You can enable this if you later define popupText logic */}
        {popupText && (
          <Popover>
            <Popover.Trigger asChild>
              <Button variant="link" className="mb-10">
                Things you should know
              </Button>
            </Popover.Trigger>
            <Popover.Content>
              <Text variant={TypographyStyles.H3}>Things you should know</Text>
              <br />
              <Text>{popupText}</Text>
            </Popover.Content>
          </Popover>
        )}

        <ChecklistDueAt
          checkListDueAt={checklistDueAt}
          displayText={displayText}
          isPastCalculationDate={isPastCalculationDate}
        />

        <IRSPaymentDueAt
          irsPaymentDueAt={checklistStatus?.rest?.forDisplay.irsPaymentDue}
          isPastPaymentDate={isPastPaymentDate}
        />
      </div>
    </div>
  )
}
